import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'

import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import * as Analytics from 'analytics/analytics'
import { propertyRateImpressionEventWithContext } from 'analytics/snowplow/events'
import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTileHeaderArea from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileHeaderArea'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import Clickable from 'components/Common/Clickable'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PayLaterPill from 'components/Common/PayLaterPill'
import type { PriceDetailsModalOfferProps } from 'components/Common/PriceDetails/PriceDetailsModal'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import PriceRowMarginText from 'components/OfferPage/PriceRowMarginText'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { SOURCE_TYPES } from 'constants/source'
import ModalContext from 'contexts/ModalContext'
import type { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import RateImpressionContext from 'contexts/RateImpressionContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import RateTierProgressionCallOutBanner from 'loyalty/components/Banners/RateTierProgressionCallOutBanner'
import usePropertyRateStatusCreditsCalculation from 'loyalty/hooks/usePropertyRateStatusCreditsCalculation'
import useHotelTierProgressionEligibility from 'loyalty/hooks/useHotelTierProgressionEligibility'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import {
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { isLEStaffOrAdmin, selectLoggedIn } from 'selectors/accountSelectors'
import { getPropertyOfferMapping } from 'selectors/offerPage/offerPageSelectors'
import { getSource } from 'selectors/offerSelectors'

import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PropertyAvailableRateOptionCtas from './PropertyAvailableRateOptionCtas'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyUnavailableRateOptionCtas from './PropertyUnavailableRateOptionCtas'
import { propertySingleRoomBookNowOrAddToCartCallback } from './propertyBuyCallback'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'

interface Props {
  'data-testid'?: string
  property: App.Property.Property
  isPropertyUltraLux: boolean
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  lpcOffer: App.HotelOffer | undefined
  rate: App.PropertyRate
  optionNumber: number // TODO one-indexed
  /** Normally the same as option number, but in the package columns A/B test it might be different */
  ratePosition?: number
  showOptionLabel: boolean
  originAirport: App.Airport | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number
  rooms: Array<App.Occupants>
  regionCode: string
  source: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
  isLoggedIn: boolean
  isSpoofed: boolean
  subscriber: App.OrderSubscriber | undefined
  canShowMargin: boolean
  canShowStaffLabel: boolean
  // TODO analytics
  roomListId: string
  roomPosition: number // TODO one-indexed
  rateListId: string
  /** Hide the tier progression banner (used when rendered inside PropertyRateOptionMerged) */
  hideTierProgressionBanner?: boolean
  cancellationPolicyContentOverride?: React.ReactNode
  showRateName?: boolean
}

function PropertyRateOption(props: Props) {
  const {
    property,
    isPropertyUltraLux,
    lpcOffer,
    rate,
    optionNumber,
    ratePosition = optionNumber,
    showOptionLabel,
    originAirport,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    source,
    utmContent,
    roomOption,
    isLoggedIn,
    isSpoofed,
    subscriber,
    canShowMargin,
    canShowStaffLabel,
    roomListId,
    roomPosition,
    rateListId,
    hideTierProgressionBanner,
    cancellationPolicyContentOverride,
    showRateName,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const impressionContext = useContext(RateImpressionContext)
  const hasAvailableFlights = rate.includesFlights && typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = useMemo(() => {
    if (!isTasteOfGoldActive || !lpcOffer) return false
    return isTasteOfGoldEligibleOffer(
      lpcOffer,
      rate.currency,
      property.location.countryCode?.toLowerCase() !== 'au',
      rate.totals.sellingPrice,
    )
  }, [isTasteOfGoldActive, lpcOffer, rate.currency, rate.totals.sellingPrice, property.location.countryCode])

  const impressionKey = useMemo(() => {
    if (hasAvailableFlights && originAirport?.code) {
      return `${rate.rateId}:flight-bundle:${originAirport.code}`
    }
    return `${rate.rateId}:hotel`
  }, [rate.rateId, hasAvailableFlights, originAirport?.code])

  const canFireImpression = !!(checkIn && checkOut && roomListId && roomPosition && rateListId)

  const [inViewRef, inView] = useInView({ skip: impressionContext.hasFired(impressionKey), threshold: 0.5 })

  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined
  const packageNumber = showOptionLabel ? optionNumber : undefined
  const rawBedbankDiscount = useBedbankDiscount(rate.totals.discount, rate.luxPlusTotals?.discount ?? 0)
  // TODO(HP-1404): Show margin details for LuxPlus+ prices.
  const shouldShowMargin = !!(canShowMargin && rate.totals.margin && rate.totals.marginAud)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(rate, {
        propertyCountryCode: property.location.countryCode,
        currentRegionCode: regionCode,
        isUltraLux: isPropertyUltraLux,
      }),
      generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
    ]
  }, [rate, property.location.countryCode, regionCode, isPropertyUltraLux])

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)

  const { statusCredits: rateStatusCredits, isLoading: statusCreditsLoading } = usePropertyRateStatusCreditsCalculation({
    rate,
    propertyCountryCode: property.location.countryCode,
    currentRegionCode: regionCode,
    isUltraLux: isPropertyUltraLux,
    nights: duration,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount,
  })

  const tierProgressionEligibility = useHotelTierProgressionEligibility({
    statusCredits: rateStatusCredits,
    nights: duration,
    checkoutDate: checkOut,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount || statusCreditsLoading,
  })

  const { showTierProgressionBanner: rawShowTierProgressionBanner, highestUnlockableTier, highestUnlockableTierName } = tierProgressionEligibility
  const showTierProgressionBanner = rawShowTierProgressionBanner && !hideTierProgressionBanner

  useEffect(() => {
    if (!canFireImpression || !inView) return
    if (impressionContext.hasFired(impressionKey)) return

    Analytics.trackEvent(propertyRateImpressionEventWithContext({
      occupancy: rooms,
      checkIn,
      checkOut,
      roomListId,
      roomPosition,
      rateListId,
      ratePosition,
      rate,
    }))
    impressionContext.markAsFired(impressionKey)
  }, [
    canFireImpression,
    checkIn,
    checkOut,
    impressionContext,
    impressionKey,
    inView,
    ratePosition,
    rate,
    rateListId,
    roomListId,
    roomPosition,
    rooms,
  ])

  const onCtaClick = useCallback(async(shouldAddToCart: boolean) => {
    if (!(checkIn && checkOut)) throw new Error('Attempted to perform cart action when dates aren’t set')
    trackEvent('BOOK_NOW_PRESSED')

    // TODO we don't care about walled garden specifics
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    propertySingleRoomBookNowOrAddToCartCallback({
      shouldAddToCart,
      dispatch,
      offerPageDispatch,
      modalElementContext,
      showModal,
      intl,
      property,
      rate,
      ratePosition,
      originAirportCode: originAirport?.code,
      checkIn,
      checkOut,
      duration,
      rooms,
      regionCode,
      source: source ? `${source},${SOURCE_TYPES.CTA}` : SOURCE_TYPES.CTA,
      utmContent,
      roomOption,
      isSpoofed,
      subscriber,
      roomListId,
      roomPosition,
      rateListId,
    })
  }, [
    checkIn,
    checkOut,
    dispatch,
    duration,
    intl,
    isLoggedIn,
    isSpoofed,
    modalElementContext,
    offerPageDispatch,
    ratePosition,
    originAirport?.code,
    property,
    rate,
    rateListId,
    regionCode,
    roomListId,
    roomOption,
    roomPosition,
    rooms,
    showModal,
    source,
    subscriber,
    trackEvent,
    utmContent,
  ])

  const handleBookNowFromModal = useCallback(() => {
    onCtaClick(false)
  }, [onCtaClick])

  const handleAddToCartFromModal = useCallback(() => {
    onCtaClick(true)
  }, [onCtaClick])

  const onToggleFilter = useCallback((filter: 'airport' | 'checkin') => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch])
  const ctas = useMemo(() => <>
    {(!rate.includesFlights || typeof rate.cheapestFlightPrice === 'number') && <PropertyAvailableRateOptionCtas
      includesFlights={rate.includesFlights}
      onCtaClick={onCtaClick}
    />}
    {rate.includesFlights && typeof rate.cheapestFlightPrice === 'string' && <PropertyUnavailableRateOptionCtas
      shouldChangeAirport={rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable'}
      shouldChangeDepartureDate={rate.cheapestFlightPrice === 'departureTooEarly' || rate.cheapestFlightPrice === 'departureTooLate'}
      onToggleFilter={onToggleFilter}
    />}
  </>, [onCtaClick, onToggleFilter, rate])

  const cancellationPolicy = <PropertyCancellationPolicy
    propertyTimeZone={property.location.timezone}
    headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
    headlineCancellationPolicyStart={headlineCancellationPolicyStart}
    fullyRefundableUntil={rate.fullyRefundableUntil}
    cancellationPolicies={rate.cancellationPolicies}
    currency={rate.currency}
    shouldClarifyRefundIsHotelOnly={rate.includesFlights}
  />

  const openPriceDetailsModal = useCallback(async() => {
    const priceDetailsModalOfferProps: PriceDetailsModalOfferProps = rate.service.type === 'lpc' ? {
      offerId: rate.service.offerId,
      offerType: OFFER_TYPE_ALWAYS_ON,
      saleUnit: 'room',
      displayPricingAsPerPerson: false,
    } : {
      offerId: rate.service.propertyId,
      offerType: OFFER_TYPE_BED_BANK,
      saleUnit: 'room',
      // TODO(HP-1614): Handle `bedbankOffer.displayPricingAsPerPerson`.
      displayPricingAsPerPerson: false,
    }
    const dueAtPropertyMessage = rate.service.type === 'lpc' ? property.taxesAndFeesContent : undefined

    // This implementation is based off `<OfferPriceDetailsRow/>`.
    // NB: Don't pass `flightPrice` to `<PriceDetailsModal/>`, as it's already baked in.
    const result = await showModal<'fine-print' | undefined>(<PriceDetailsModal
      {...priceDetailsModalOfferProps}
      offerPage
      duration={duration}
      taxesAndFees={rate.totals.taxesAndFees}
      propertyFees={rate.totals.propertyFees}
      hotelPrice={rate.totals.sellingPrice}
      hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
      showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
      rooms={rooms}
      dueAtPropertyMessage={dueAtPropertyMessage}
      cta={
        <Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
          {ctas}
        </Group>
      }
    />)
    if (result === 'fine-print') {
      window.location.href = '#fine-print'
    }
  }, [ctas, duration, property, rate, rooms, showModal])
  const taxesAndFeesContent = <Clickable onClick={openPriceDetailsModal}>
    <Caption variant="medium" colour="neutral-three" underline>
      {intl.formatMessage({
        id: 'H29/x+SS95',
        defaultMessage: 'Includes taxes & fees',
        description: 'Label indicating that taxes and fees are included in the price',
      })}
    </Caption>
  </Clickable>

  const noFlightsAvailableMessage = intl.formatMessage(
    {
      id: 'KCe0399qOg',
      defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
      description: 'Error message when no flights are available from the selected airport',
    },
    { airportName: originAirport?.name },
  )
  const flightsWithin24HoursMessage = intl.formatMessage({
    id: 'WwEhobNYMc',
    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
    description: 'Error message when departure is too soon for online flight booking',
  })
  const flightsAdvanceBookingMessage = intl.formatMessage({
    id: '0j8ptAKnep',
    defaultMessage: 'Flights can only be booked up to 11 months in advance.',
    description: 'Error message when departure date is too far in advance for flight booking',
  })
  const addFlightsMessage = intl.formatMessage({
    id: 'OrJCJ4isDF',
    defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
    description: 'Info message about adding flights to the hotel package at checkout',
  })

  const staffRateLabel = canShowStaffLabel && rate.staffLabel ? intl.formatMessage({
    id: 'HJnIuZ3JW8',
    defaultMessage: '{staffLabel} (label only shows for staff)',
    description: 'Staff-only label providing additional rate details',
  }, { staffLabel: rate.staffLabel }) : undefined

  /**
   * LPC RATES
   */
  if (rate.service.type === 'lpc') {
    const shouldShowLimitedTimeSpecialLabel = rate.service.isTactical || rate.hasAppliedDiscount
    const lpcDiscount = rate.totals.discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rate.totals.discount : 0
    const shouldShowValue = rate.totals.valuedUpToPrice > rate.totals.sellingPrice

    const lpcDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'WlUcNFNb6H',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for LPC property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: 'yOdEqLa274',
            defaultMessage: '{count, plural, one {# night} other {# nights}}',
            description: 'Duration in nights for LPC property rate option',
          },
          { count: duration },
        )

    return (
      <AccommodationSameRoomOptionTile data-testid={rate.service.rateId} ref={inViewRef}>
        {showTierProgressionBanner && highestUnlockableTier && (
          <AccommodationSameRoomOptionTileHeaderArea>
            <RateTierProgressionCallOutBanner
              highestUnlockableTier={highestUnlockableTier}
              highestUnlockableTierName={highestUnlockableTierName}
              onBookNow={handleBookNowFromModal}
              onAddToCart={handleAddToCartFromModal}
              checkoutDate={checkOut}
            />
          </AccommodationSameRoomOptionTileHeaderArea>
        )}
        <AccommodationSameRoomOptionTileDetails
          packageNumber={packageNumber}
          optionName={showRateName ? rate.name : undefined}
          tagLineLabel={<>
            {shouldShowLimitedTimeSpecialLabel && <ProductTypeLabel productType="limited_time_special" />}
          </>}
        >
          {cancellationPolicyContentOverride || <Group direction="vertical" gap={4} noDisplayWhenEmpty>
            {cancellationPolicy}
            {staffRateLabel && (
              <BodyText variant="medium" colour="neutral-three">
                {staffRateLabel}
              </BodyText>
            )}
          </Group>}
          {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
          <LargeContentToggle
            maxHeight={208}
            showMoreText={intl.formatMessage({
              id: '74uMot/Lhp',
              defaultMessage: 'View more inclusions',
              description: 'Toggle text to expand inclusions list for property rate option',
            })}
            showLessText={intl.formatMessage({
              id: 'i/MD93nsLR',
              defaultMessage: 'View less inclusions',
              description: 'Toggle text to collapse inclusions list for property rate option',
            })}
          >
            <VerticalSpacer gap={12}>
              {rate.luxPlusInclusions.length > 0 && <LuxPlusInclusions
                luxPlusInclusions={rate.luxPlusInclusions}
                accommodationOffer={lpcOffer}
              />}
              {rate.bonusInclusions.length > 0 && <OfferPagePackageInclusionsList title="bonus">
                {rate.bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
              </OfferPagePackageInclusionsList>}
              {rate.inclusions.length > 0 && <VerticalSpacer gap={4}>
                {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
              </VerticalSpacer>}
            </VerticalSpacer>
          </LargeContentToggle>
        </AccommodationSameRoomOptionTileDetails>

        <AccommodationSameRoomOptionTilePricing>
          {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4} wrap="wrap" noDisplayWhenEmpty>
            {lpcDiscount > 0 && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={lpcDiscount}
            />}
          </Group>}
          {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
          {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
            <div>
              <Caption variant="medium">
                <Group direction="vertical" gap={4}>
                  <div>
                    <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                      {lpcDurationLabel}
                    </PriceRowPriceCaption>
                    <LuxPlusPriceGuard
                      showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
                      fallbackPrice={<PriceRowPrice
                        data-testid="property-package-price"
                        size="M"
                        price={rate.totals.displayPrice}
                        saleUnit="total"
                      />}
                    >
                      <LuxPlusPriceStack
                        size="M"
                        testid="property-package-price"
                        price={rate.totals.displayPrice}
                        memberPrice={rate.luxPlusTotals?.displayPrice ?? 0}
                        saleUnit="total"
                      />
                    </LuxPlusPriceGuard>
                    {shouldShowValue && <PriceRowValueDiscountWithCaption
                      size="M"
                      originalValue={rate.totals.valuedUpToPrice}
                    />}
                    {taxesAndFeesContent}
                  </div>
                  {rate.exceedsIncludedGuests && <AdditionalGuestsPopup
                    complex={false}
                    description={intl.formatMessage({
                      id: '3KcTGG2Q7z',
                      defaultMessage: '+ Extra guest surcharge',
                      description: 'Description for additional guests popup in property rate option',
                    })}
                    modalContent={rate.includedGuestsExceededPolicy}
                  />}
                  {shouldShowMargin && <PriceRowMarginText
                    marginValue={rate.totals.marginAud!}
                    margin={rate.totals.margin}
                    type={rate.includesFlights ? 'property_estimate_accom' : 'property_estimate'}
                  />}
                </Group>
              </Caption>
            </div>
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequests}
              calculationType={rate.includesFlights ? 'estimate' : 'exact'}
            />
          </VerticalSpacer>}
          {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <VerticalSpacer gap={12}>
            {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="neutral-two" variant="small">
              {noFlightsAvailableMessage}
            </BodyText>}
            {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="neutral-two" variant="small">
              {flightsWithin24HoursMessage}
            </BodyText>}
            {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="neutral-two" variant="small">
              {flightsAdvanceBookingMessage}
            </BodyText>}
            {rate.cheapestFlightPrice === 0 && <BodyText colour="neutral-two" variant="small">
              {addFlightsMessage}
            </BodyText>}
          </VerticalSpacer>}
          <PriceRowAgentHubCommission
            offerId={rate.service.offerId}
            productType={lpcOffer?.productType ?? 'hotel'}
            offerTotal={rate.totals.sellingPrice}
            packageId={rate.service.packageId}
          />
        </AccommodationSameRoomOptionTilePricing>

        <AccommodationSameRoomOptionTileCTA>
          {ctas}
        </AccommodationSameRoomOptionTileCTA>
      </AccommodationSameRoomOptionTile>
    )
  }

  /**
   * BEDBANK RATES
   */
  if (rate.service.type === 'bedbank') {
    const bedbankDiscount = rawBedbankDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rawBedbankDiscount : 0

    const bedbankDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'qkFlsC5yUK',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for bedbank property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: '00Dl9U8/kb',
            defaultMessage: '{count, plural, one {# night} other {# nights}} from',
            description: 'Duration in nights for bedbank property rate option',
          },
          { count: duration },
        )

    return (
      <AccommodationSameRoomOptionTile ref={inViewRef}>
        {showTierProgressionBanner && highestUnlockableTier && (
          <AccommodationSameRoomOptionTileHeaderArea>
            <RateTierProgressionCallOutBanner
              highestUnlockableTier={highestUnlockableTier}
              highestUnlockableTierName={highestUnlockableTierName}
              onBookNow={handleBookNowFromModal}
              onAddToCart={handleAddToCartFromModal}
              checkoutDate={checkOut}
            />
          </AccommodationSameRoomOptionTileHeaderArea>
        )}
        <AccommodationSameRoomOptionTileDetails packageNumber={packageNumber}>
          {cancellationPolicyContentOverride || <Group direction="vertical" gap={4} noDisplayWhenEmpty>
            {cancellationPolicy}
            {staffRateLabel && (
              <BodyText variant="medium" colour="neutral-three">
                {staffRateLabel}
              </BodyText>
            )}
          </Group>}
          {/* TODO !!rate.promotions.length && <div></div> */}
          {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
          {rate.inclusions.length > 0 && (
            <VerticalSpacer gap={4}>
              {rate.inclusions.map(inclusion => (
                <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />
              ))}
            </VerticalSpacer>
          )}
        </AccommodationSameRoomOptionTileDetails>

        <AccommodationSameRoomOptionTilePricing>
          {(!rate.includesFlights ||
            (typeof rate.cheapestFlightPrice === 'number' &&
              rate.cheapestFlightPrice > 0)) && (
            <Group direction="horizontal" gap={4} noDisplayWhenEmpty>
              {bedbankDiscount > 0 && (
                <PriceRowValueDiscount
                  type="estimate"
                  discountPercentage={bedbankDiscount}
                />
              )}
              {rate.hasPaymentSchedules && <PayLaterPill />}
            </Group>
          )}

          {rate.includesFlights &&
            rate.cheapestFlightPrice === 'loading' && (
            <LoadingIndicator floating opaque />
          )}

          {(!rate.includesFlights ||
            (typeof rate.cheapestFlightPrice === 'number' &&
              rate.cheapestFlightPrice > 0)) && (
            <VerticalSpacer gap={12}>
              <div>
                <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                  {bedbankDurationLabel}
                </PriceRowPriceCaption>
                <LuxPlusPriceGuard
                  showMemberPrice={
                    (rate.luxPlusTotals?.displayPrice ?? 0) > 0
                  }
                  fallbackPrice={
                    <PriceRowPrice
                      data-testid="property-package-price"
                      size="M"
                      price={rate.totals.displayPrice}
                      saleUnit="total"
                    />
                  }
                >
                  <LuxPlusPriceStack
                    size="M"
                    testid="property-package-price"
                    price={rate.totals.displayPrice}
                    memberPrice={rate.luxPlusTotals?.displayPrice ?? 0}
                    saleUnit="total"
                  />
                </LuxPlusPriceGuard>

                {!!rawBedbankDiscount && (
                  <PriceRowValueDiscountWithCaption
                    size="M"
                    originalValue={rate.totals.valuedUpToPrice}
                  />
                )}

                {taxesAndFeesContent}

                {shouldShowMargin && (
                  <PriceRowMarginText
                    marginValue={rate.totals.marginAud!}
                    margin={rate.totals.margin}
                    supplier={rate.service.supplier}
                    type={
                      rate.includesFlights
                        ? 'property_estimate_accom'
                        : 'property_estimate'
                    }
                  />
                )}
              </div>

              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={
                  rate.includesFlights ? 'estimate' : 'exact'
                }
              />
            </VerticalSpacer>
          )}

          {rate.includesFlights &&
            (typeof rate.cheapestFlightPrice === 'string' ||
              rate.cheapestFlightPrice === 0) && (
            <VerticalSpacer gap={12}>
              {(rate.cheapestFlightPrice === 'originSameAsDestination' ||
                rate.cheapestFlightPrice === 'unavailable') && (
                <BodyText colour="neutral-two" variant="small">
                  {noFlightsAvailableMessage}
                </BodyText>
              )}

              {rate.cheapestFlightPrice === 'departureTooEarly' && (
                <BodyText colour="neutral-two" variant="small">
                  {flightsWithin24HoursMessage}
                </BodyText>
              )}

              {rate.cheapestFlightPrice === 'departureTooLate' && (
                <BodyText colour="neutral-two" variant="small">
                  {flightsAdvanceBookingMessage}
                </BodyText>
              )}

              {rate.cheapestFlightPrice === 0 && (
                <BodyText colour="neutral-two" variant="small">
                  {addFlightsMessage}
                </BodyText>
              )}
            </VerticalSpacer>
          )}
          <PriceRowAgentHubCommission
            offerId={rate.service.propertyId}
            productType="bedbank_hotel"
            offerTotal={rate.totals.sellingPrice}
            packageId={rate.rateId}
          />
        </AccommodationSameRoomOptionTilePricing>

        <AccommodationSameRoomOptionTileCTA>
          {ctas}
        </AccommodationSameRoomOptionTileCTA>
      </AccommodationSameRoomOptionTile>
    )
  }

  return null
}

export default withOfferPageAndReduxState((state, appState) => ({
  property: state.property!,
  isPropertyUltraLux: getPropertyOfferMapping(state, appState)?.isUltraLux ?? false,
  originAirport: state.airport,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  rooms: state.rooms,
  regionCode: appState.geo.currentRegionCode,
  source: getSource(appState),
  utmContent: appState.router.location.query.utm_content,
  roomOption: state.roomOption,
  isLoggedIn: selectLoggedIn(appState),
  isSpoofed: appState.auth.account.isSpoofed,
  subscriber: getEnquirySubscriber(appState),
  canShowMargin: appState.auth.account.isSpoofed && appState.system.showMarginInSpoofMode,
  canShowStaffLabel: isLEStaffOrAdmin(appState) || appState.auth.account.isSpoofed,
  open: !!state.activeFilter,
}), PropertyRateOption, preventRenderWithOpenFilter)
